import React, { useState, useEffect, useCallback } from 'react';
import { Formik, Field, Form } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { createOrder, updateOrder, fetchOrders } from 'redux/order/orderSlice';
import {
  Box,
  Grid,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Card,
  Button,
  Divider,
  Checkbox,
  FormControlLabel,
  Chip,
  CardContent,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { Autocomplete, TextField } from 'formik-mui';
import MenuItem from '@mui/material/MenuItem';
import SearchIcon from '@mui/icons-material/Search';
import MuiTextField from '@mui/material/TextField';
import { useLocation, useNavigate } from 'react-router-dom';
import authAxios from 'utils/authAxios';
import { debounce } from 'lodash';
import CloseIcon from '@mui/icons-material/Close';
import { enqueueSnackbar } from 'notistack';

import webSocketManager from 'utils/WebSocketManager';
import { useWebTrader } from 'utils/WebTraderProvider';
import { fetchTrades } from 'redux/trade/tradeSlice';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { routes } from 'routes';




const TradePanel = ({ strategyId, tradeType }) => {
  const dispatch = useDispatch();
  const ordersLoading = useSelector(state => state.order.ordersLoading);
  const location = useLocation();
  const navigate = useNavigate();
  const [isAdvancedVisible, setAdvancedVisible] = useState(false);
  const [latestSymbols, setLatestSymbols] = useState([]);
  const [ws, setWs] = useState(null);
  // const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();
  const [searchOptions, setSearchOptions] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Options-specific state
  const [optionsChainData, setOptionsChainData] = useState(null);
  const [isLoadingOptionsChain, setIsLoadingOptionsChain] = useState(false);
  const [selectedStrike, setSelectedStrike] = useState('');
  const [selectedExpiry, setSelectedExpiry] = useState('');
  const [selectedRight, setSelectedRight] = useState('C'); // 'C' for Call, 'P' for Put

  const { 
    selectedSymbol,
    setSelectedSymbol,
    marketData,
    isModifyingOrder,
    orderToModify,
    cancelOrderModification,
    wsConnected
  } = useWebTrader();

  const symbolMarketData = selectedSymbol?.conid ? marketData[selectedSymbol.conid] : null;


  const tradeTypeLabels = {
    stocks: 'EQ',
    futures: 'FU',
    forex: 'FX',
    options: 'OPT'
  };




  // Load latest symbols from localStorage when the component mounts
  useEffect(() => {
    const storedSymbols = localStorage.getItem(`latestSymbols_${tradeType}`);
    if (storedSymbols) {
      setLatestSymbols(JSON.parse(storedSymbols));
    }
  }, [tradeType]);

  // Function to update the latest symbols in localStorage
  const updateLatestSymbols = (selectedSymbol) => {
    let updatedSymbols = [...latestSymbols];
    const exists = updatedSymbols.find((symbol) => symbol.conid === selectedSymbol.conid);

    if (!exists) {
      updatedSymbols = [selectedSymbol, ...updatedSymbols.slice(0, 4)];
      setLatestSymbols(updatedSymbols);
      localStorage.setItem(`latestSymbols_${tradeType}`, JSON.stringify(updatedSymbols));
    }
  };

  // Function to fetch symbols from API
  const fetchSymbols = async (searchQuery) => {
    if (!searchQuery?.trim()) {
      setSearchOptions([]);
      return;
    }
    setSearchOptions([]);

    setIsLoading(true);
    try {
      // Use dynamic endpoint based on trade type
      const endpoint = `/broker/interactive_brokers/search/${tradeType}/`;
      
      const response = await authAxios.get(endpoint, {
        params: {
          search: searchQuery
        }
      });
      
      // Process results based on trade type
      let processedSymbols;
      if (tradeType === 'futures') {
        processedSymbols = response.data.map(item => ({
          label: `${item.symbol} ${formatContractMonth(item.lastTradeDateOrContractMonth)}`,
          name: item.description || item.name,
          currency: item.currency,
          exchange: item.listingExchange || item.exchange,
          conid: item.conid,
          symbol: item.symbol,
          lastTradeDateOrContractMonth: item.lastTradeDateOrContractMonth,
          multiplier: item.multiplier,
          displayName: `${item.symbol} ${formatContractMonth(item.lastTradeDateOrContractMonth)} (${item.listingExchange || item.exchange})`
        }));
        
        // Sort futures by expiration date (nearest first)
        processedSymbols.sort((a, b) => (a.lastTradeDateOrContractMonth || '99999999').localeCompare(b.lastTradeDateOrContractMonth || '99999999'));
      } else if (tradeType === 'forex') {
        // Forex processing - currency pairs
        processedSymbols = response.data.map(item => ({
          label: item.symbol,
          name: item.description || item.symbol,
          currency: item.currency,
          exchange: item.listingExchange || item.exchange,
          conid: item.conid,
          symbol: item.symbol,
          displayName: `${item.symbol} (${item.listingExchange || item.exchange})`
        }));
        
        // Sort forex pairs alphabetically
        processedSymbols.sort((a, b) => a.symbol.localeCompare(b.symbol));
      } else if (tradeType === 'options') {
        // Options processing - search returns underlying stocks
        processedSymbols = response.data.map(item => ({
          label: item.ticker,
          name: item.name,
          currency: item.currency,
          exchange: item.listingExchange,
          conid: item.conid,
          optionsSearchable: item.optionsSearchable || false,
          displayName: `${item.name} (${item.listingExchange})`
        }));
      } else {
        // Stocks processing (existing logic)
        processedSymbols = response.data.map(item => ({
          label: item.ticker,
          name: item.name,
          currency: item.currency,
          exchange: item.listingExchange,
          conid: item.conid
        }));
      }
      
      setSearchOptions(processedSymbols);
    } catch (error) {
      
    } finally {
      setIsLoading(false);
      setIsTyping(false);
    }
  };

  // Function to load options chain for selected underlying stock
  const loadOptionsChain = async (underlyingConid, selectedSymbolData, setFieldValue, setSelectedSymbol) => {
    if (!underlyingConid) return;
    
    setIsLoadingOptionsChain(true);
    try {
      const endpoint = `/broker/interactive_brokers/options-chain/${underlyingConid}/`;
      const response = await authAxios.get(endpoint);
      
      
      // Check for error in response data (no options available)
      // Backend returns either explicit error or empty expirations/strikes arrays
      if (response.data.error || 
          !response.data.expirations || 
          response.data.expirations.length === 0 || 
          !response.data.strikes || 
          Object.keys(response.data.strikes).length === 0) {
        
        const symbolName = selectedSymbolData?.name || selectedSymbolData?.label || 'this equity';
        const exchangeName = selectedSymbolData?.exchange || 'this exchange';
        enqueueSnackbar(`No options available for selected equity.`, { 
          variant: 'warning',
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right'
          },
          autoHideDuration: 5000
        });
        
        // Reset symbol selection and clear option parameters
        setFieldValue('symbol', null);
        setSelectedSymbol(null);
        setSelectedStrike('');
        setSelectedExpiry('');
        setSelectedRight('C');
        setOptionsChainData(null);
        return;
      }
      
      setOptionsChainData(response.data);
      
      // Ensure default selections are always set when data is available
      if (response.data.defaultSelection && response.data.defaultSelection.strike && response.data.defaultSelection.expiry) {
        // Use backend's default selection
        setSelectedStrike(response.data.defaultSelection.strike);
        setSelectedExpiry(response.data.defaultSelection.expiry);
        setSelectedRight(response.data.defaultSelection.right || 'C');
      } else if (response.data.expirations && response.data.expirations.length > 0) {
        // Fallback: auto-select first available expiry and middle strike
        const firstExpiry = response.data.expirations[0];
        const availableStrikes = response.data.strikes[firstExpiry];
        
        if (availableStrikes && availableStrikes.length > 0) {
          // Select middle strike price (closest to ATM concept)
          const middleIndex = Math.floor(availableStrikes.length / 2);
          const selectedStrikeValue = availableStrikes[middleIndex];
          
          setSelectedStrike(selectedStrikeValue);
          setSelectedExpiry(firstExpiry);
          setSelectedRight('C');
        }
      }
      
    } catch (error) {
      setOptionsChainData(null);
    } finally {
      setIsLoadingOptionsChain(false);
    }
  };

  // Helper function to format contract month (handles various date formats)
  const formatContractMonth = (dateString) => {
    if (!dateString) return '';
    
    try {
      const dateStr = String(dateString);
      
      // Handle full date format: 20231215
      if (dateStr.length === 8) {
        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(4, 6);
        
        const monthNames = {
          '01': 'JAN', '02': 'FEB', '03': 'MAR', '04': 'APR',
          '05': 'MAY', '06': 'JUN', '07': 'JUL', '08': 'AUG',
          '09': 'SEP', '10': 'OCT', '11': 'NOV', '12': 'DEC'
        };
        
        const monthName = monthNames[month] || month;
        const shortYear = year.substring(2, 4);
        
        return `${monthName}${shortYear}`;
      }
      
      // Handle year-month format: 202312
      if (dateStr.length === 6) {
        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(4, 6);
        
        const monthNames = {
          '01': 'JAN', '02': 'FEB', '03': 'MAR', '04': 'APR',
          '05': 'MAY', '06': 'JUN', '07': 'JUL', '08': 'AUG',
          '09': 'SEP', '10': 'OCT', '11': 'NOV', '12': 'DEC'
        };
        
        const monthName = monthNames[month] || month;
        const shortYear = year.substring(2, 4);
        
        return `${monthName}${shortYear}`;
      }
      
      // Handle format like "2025-09"
      if (dateStr.includes('-')) {
        const [year, month] = dateStr.split('-');
        
        const monthNames = {
          '01': 'JAN', '02': 'FEB', '03': 'MAR', '04': 'APR',
          '05': 'MAY', '06': 'JUN', '07': 'JUL', '08': 'AUG',
          '09': 'SEP', '10': 'OCT', '11': 'NOV', '12': 'DEC'
        };
        
        const paddedMonth = month.padStart(2, '0');
        const monthName = monthNames[paddedMonth] || month;
        const shortYear = year.length === 4 ? year.substring(2, 4) : year;
        
        return `${monthName}${shortYear}`;
      }
      
      // Fallback: return as is
      return dateStr;
      
    } catch (error) {
      return dateString;
    }
  };

  // Helper function to format expiration date for display (converts "20250321" to "Mar 21, 2025")
  const formatExpirationDate = (dateString) => {
    if (!dateString || dateString.length < 8) return '';
    
    try {
      const year = dateString.substring(0, 4);
      const month = dateString.substring(4, 6);
      const day = dateString.substring(6, 8);
      
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (error) {
      return dateString;
    }
  };

  // Modify initial values to use orderToModify data when in modification mode
  const initialValues = isModifyingOrder ? {
    symbol: {
      label: orderToModify.data.symbol,
      conid: orderToModify.data.conid,
      name: orderToModify.coin_info.company_name,
      exchange: orderToModify.coin_info.listing_exchange,
      currency: orderToModify.coin_info.currency
    },
    side: orderToModify.data.side,
    order_type: orderToModify.data.order_type,
    quantity: orderToModify.data.quantity,
    price: orderToModify.data.price || '',
    tif: orderToModify.data.tif.toLowerCase(),
    stop_loss: '',
    profit_target: '',
    order_comment: '',
    isStopLossEnabled: false,
    isProfitTargetEnabled: false,
  } : {
    symbol: null,
    side: 'BUY',
    order_type: 'MKT',
    quantity: 0,
    price: '',
    tif: 'day',
    stop_loss: '',
    profit_target: '',
    order_comment: '',
    isStopLossEnabled: false,
    isProfitTargetEnabled: false,
  };

  // Validation schema
  const validationSchema = Yup.object().shape({
    symbol: Yup.object().required(
      tradeType === 'futures' ? 'Futures symbol is required' : 
      tradeType === 'forex' ? 'Forex symbol is required' : 
      tradeType === 'options' ? 'Underlying equity symbol is required' :
      'Equity symbol is required'
    ),
    quantity: Yup.number().required('Quantity is required').min(1, 'Quantity must be at least 1'),
    price: Yup.number().when('order_type', {
      is: (val) => val !== 'MKT',
      then: () => Yup.number().required('Price is required').min(0.01, 'Price must be greater than 0'),
      otherwise: () => Yup.number(),
    }),
    order_type: Yup.string().required('Order Type is required'),
    stop_loss: Yup.number().when(['isStopLossEnabled', 'side', 'order_type'], {
      is: (isEnabled, side, orderType) => isEnabled === true,
      then: () => Yup.number()
        .required('Stop Loss is required')
        .min(0.01, 'Stop Loss must be greater than 0')
        .test('stop-loss-validation', 'Invalid Stop Loss price', function(value) {
          const { side, order_type, price } = this.parent;
          
          if (!value) return true; // Skip validation if no stop loss value
          
          // Use market price for MKT orders, limit price for LMT orders
          const referencePrice = order_type === 'MKT' 
            ? symbolMarketData?.bid_price 
            : parseFloat(price);
            
          if (!referencePrice) return true; // Skip if no reference price available

          if (side === 'BUY') {
            if (value >= referencePrice) {
              return this.createError({
                message: `Stop Loss must be below the ${order_type === 'MKT' ? 'market' : 'limit'} price for BUY orders`
              });
            }
          } else if (side === 'SELL') {
            if (value <= referencePrice) {
              return this.createError({
                message: `Stop Loss must be above the ${order_type === 'MKT' ? 'market' : 'limit'} price for SELL orders`
              });
            }
          }
          return true;
        }),
      otherwise: () => Yup.number().nullable(),
    }),
    profit_target: Yup.number().when(['isProfitTargetEnabled', 'side', 'order_type'], {
      is: (isEnabled, side, orderType) => isEnabled === true,
      then: () => Yup.number()
        .required('Profit Target is required')
        .min(0.01, 'Profit Target must be greater than 0')
        .test('take-profit-validation', 'Invalid Take Profit price', function(value) {
          const { side, order_type, price } = this.parent;
          
          if (!value) return true; // Skip validation if no take profit value
          
          // Use market price for MKT orders, limit price for LMT orders
          const referencePrice = order_type === 'MKT' 
            ? symbolMarketData?.bid_price 
            : parseFloat(price);
            
          if (!referencePrice) return true; // Skip if no reference price available

          if (side === 'BUY') {
            if (value <= referencePrice) {
              return this.createError({
                message: `Take Profit must be above the ${order_type === 'MKT' ? 'market' : 'limit'} price for BUY orders`
              });
            }
          } else if (side === 'SELL') {
            if (value >= referencePrice) {
              return this.createError({
                message: `Take Profit must be below the ${order_type === 'MKT' ? 'market' : 'limit'} price for SELL orders`
              });
            }
          }
          return true;
        }),
      otherwise: () => Yup.number().nullable(),
    }),
    stopLossEnabled: Yup.boolean(),
    profitTargetEnabled: Yup.boolean(),
    order_comment: Yup.string().notRequired(),
  });

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const orderData = {
        conid: values.symbol.conid,
        symbol: values.symbol.label,
        side: values.side,
        order_type: values.order_type,
        quantity: values.quantity,
        price: values.order_type === 'MKT' ? null : values.price,
        tif: values.tif,
        stop_loss: values.isStopLossEnabled ? values.stop_loss : null,
        profit_target: values.isProfitTargetEnabled ? values.profit_target : null,
        order_comment: values.order_comment,
      };

      // Add security type based on trade type
      if (tradeType === 'options') {
        orderData.strike = selectedStrike;
        orderData.expiry = selectedExpiry;
        orderData.right = selectedRight; // 'C' for Call, 'P' for Put
        orderData.sec_type = 'OPT';
      } else if (tradeType === 'futures') {
        orderData.sec_type = 'FUT';
      } else if (tradeType === 'forex') {
        orderData.sec_type = 'CASH';
      } else {
        // Default to stocks
        orderData.sec_type = 'STK';
      }

      if (isModifyingOrder) {
        const response = await dispatch(updateOrder({
          strategyId,
          orderId: orderToModify.id,
          orderData: orderData
        })).unwrap();

        
        const orderDetails = `${values.side} ${values.quantity} ${values.symbol.label} @ ${values.order_type} ${values.tif.toUpperCase()}`;
        const message = (
          <Box>
            <Typography variant="subtitle2">Order Modified</Typography>
            <Typography variant="body2">{orderDetails}</Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>Signal ID: {orderToModify.id} {'->'} {response[0].id}</Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>Strategy ID: {strategyId}</Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>{new Date().toLocaleString()}</Typography>
          </Box>
        );
        
        enqueueSnackbar(message, { 
          variant: 'success',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center'
          },
          autoHideDuration: 5000
        });
        cancelOrderModification();
      } else {
        const response = await dispatch(createOrder({ 
          strategyId, 
          orderData: orderData 
        })).unwrap();
        
        // Format the notification message
        const orderDetails = `${values.side} ${values.quantity} ${values.symbol.label} @ ${values.order_type} ${values.tif.toUpperCase()}`;
        const message = (
          <Box>
            <Typography variant="subtitle2">Order Placed</Typography>
            <Typography variant="body2">{orderDetails}</Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>Signal ID: {response[0].id}</Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>Strategy ID: {strategyId}</Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>{new Date().toLocaleString()}</Typography>
          </Box>
        );
        
        enqueueSnackbar(message, { 
          variant: 'success',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center'
          },
          autoHideDuration: 5000
        });
        resetForm();
        setSelectedSymbol(null); // Reset the selected symbol to clear market data display
        setSearchOptions([]); // Clear search options
        setSearchText(''); // Clear search text
      }

      // Skip immediate refresh, just dispatch trades update
      dispatch(fetchTrades({ strategyId }));
      
      // Force a refresh after a longer delay to ensure we have the latest data
      setTimeout(() => {
        dispatch(fetchOrders({ strategyId, refresh: true }));
      }, 1000);
      
    } catch (error) {
    } finally {
      setSubmitting(false);
    }
  };

  // Cleanup effect when component unmounts
  useEffect(() => {
    return () => {
      setSelectedSymbol(null); // Reset selectedSymbol on unmount
      // setLastPrice('N/A');
      // setChangePercent('N/A');
      // setVolume('N/A');
    };
  }, [setSelectedSymbol]);

  // Update button text based on modification state
  const getButtonText = (side) => {
    if (isModifyingOrder) {
      return 'Modify Order';
    }
    return side === 'BUY' ? 'Place Buy Order' : 'Place Sell Order';
  };

  // First, update the useEffect to close advanced tab when modifying order
  useEffect(() => {
    if (isModifyingOrder) {
      setAdvancedVisible(false);
    }
  }, [isModifyingOrder]);

  return (
    <>
      <Formik 
        initialValues={initialValues} 
        validationSchema={validationSchema} 
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({ values, setFieldValue, isSubmitting, errors, touched }) => (
          <Form>
            {isModifyingOrder && (
              <Card variant="elevation" elevation={2} sx={{ p: 1 }}>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="body2" textAlign="center">
                    MODIFYING ORDER - {orderToModify.id}
                  </Typography>
                  <IconButton 
                    size="small" 
                    onClick={cancelOrderModification}
                    sx={{ ml: 1 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Card>
            )}
            <Box>
              {/* Search Section */}
              <Box sx={{ mt: 2 }}>
                {!isModifyingOrder ? (
                  <>
                    <Field
                      component={Autocomplete}
                      name="symbol"
                      options={searchOptions}
                      loading={isLoading}
                      getOptionLabel={(option) => option.label}
                      popupIcon={<SearchIcon />}
                      sx={{
                        '.MuiIconButton-root': {
                          transform: 'none!important',
                        },
                      }}
                      isOptionEqualToValue={(option, value) => option.label === value?.label}
                      filterOptions={(x) => x}
                      onInputChange={(event, value, reason) => {
                        setSearchText(value);
                        setSearchOptions([]);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' && searchText) {
                          event.preventDefault();
                          
                          fetchSymbols(searchText);
                          
                        }
                      }}
                      renderOption={(props, option) => (
                        <Box {...props} sx={{ display: 'flex', alignItems: 'center' }}>
                          <Chip
                            label={tradeTypeLabels[tradeType] || tradeType?.toUpperCase() || 'UNKNOWN'}
                            color="warning"
                            size="small"
                            sx={{ borderRadius: '4px', mr: 2 }}
                          />
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body2" fontWeight="bold">
                              {option.name}
                            </Typography>
                            <Typography variant="caption">
                              {option.currency} • {option.label}
                              {tradeType === 'futures' && option.multiplier && (
                                <> • Multiplier: {option.multiplier}</>
                              )}
                            </Typography>
                            {tradeType === 'futures' && option.lastTradeDateOrContractMonth && (
                              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                Expires: {formatExpirationDate(option.lastTradeDateOrContractMonth)}
                              </Typography>
                            )}
                          </Box>
                          <Chip 
                            label={option.exchange} 
                            size="small" 
                            sx={{ ml: 2, fontSize: '0.75rem' }} 
                          />
                        </Box>
                      )}
                      renderInput={(params) => (
                        <MuiTextField
                          {...params}
                          name="symbol"
                          error={touched['symbol'] && !!errors['symbol']}
                          helperText={
                            isTyping && searchText 
                              ? 'Press Enter to search' 
                              : (touched['symbol'] && errors['symbol'])
                          }
                          label={
                            tradeType === 'futures' ? 'Futures symbol' : 
                            tradeType === 'forex' ? 'Forex symbol' : 
                            'Equity symbol'
                          }
                          placeholder="Type and press Enter to search"
                          InputProps={{
                            ...params.InputProps,
                          }}
                          size="small"
                        />
                      )}
                      onChange={(event, newValue) => {
                        setFieldValue('symbol', newValue);
                        setSelectedSymbol(newValue);
                        if (newValue) {
                          updateLatestSymbols(newValue);
                          
                          // Load options chain if in options mode
                          if (tradeType === 'options') {
                            loadOptionsChain(newValue.conid, newValue, setFieldValue, setSelectedSymbol);
                          }
                        } else {
                          // Clear options data when symbol is cleared
                          if (tradeType === 'options') {
                            setSelectedStrike('');
                            setSelectedExpiry('');
                            setSelectedRight('C');
                            setOptionsChainData(null);
                          }
                        }
                        setSearchOptions([]);
                        setSearchText('');
                      }}
                    />
                    {/* LATEST Section */}
                    <Typography variant="caption" display="block" sx={{ mt: 1, minHeight: 20 }}>
                      LATEST:&nbsp;
                      <Box sx={{ml:6, mt:-3}}>
                      {latestSymbols.map((symbol) => (
                        <Chip
                          key={symbol.conid}
                          label={`${symbol.label} • ${symbol.exchange}`}
                          onClick={() => {
                            setSelectedSymbol(symbol);
                            setFieldValue('symbol', symbol);
                            
                            // Load options chain if in options mode
                            if (tradeType === 'options') {
                              loadOptionsChain(symbol.conid, symbol, setFieldValue, setSelectedSymbol);
                            }
                          }}
                          size="small"
                          sx={{ 
                            fontSize: '0.75rem', 
                            borderRadius: '4px', 
                            marginRight: '4px', 
                            marginTop: '4px',
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'action.hover',
                            },
                          }}
                        />
                        
                      ))}</Box>
                    </Typography>
                  </>
                ) : null}
              </Box>

              {/* Security Info Section */}
              <Card variant="outlined" sx={{ mt: 2, mb: 4, p: 0, opacity: values.symbol ? 1 : 0.5 }}>
                <CardContent sx={{ p: '8px!important' }}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Chip
                      label={tradeTypeLabels[tradeType] || tradeType?.toUpperCase() || 'UNKNOWN'}
                      color= {values.symbol ? 'warning' : 'default'}
                      size="medium"
                      sx={{ borderRadius: '4px', mr: 1 }}
                    />
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {values.symbol ? values.symbol.name : 'SYMBOL NAME'}
                      </Typography>
                      <Typography variant="caption">
                        {values.symbol ? `${values.symbol.label} • ${values.symbol.currency}` : 'SYMBOL • USD'}
                      </Typography>
                      {tradeType === 'futures' && values.symbol?.lastTradeDateOrContractMonth && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                          Expires: {formatExpirationDate(values.symbol.lastTradeDateOrContractMonth)}
                          {values.symbol.multiplier && ` • Multiplier: ${values.symbol.multiplier}`}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Divider />

                  <Grid container justifyContent="space-between" alignItems="center" my={1}>
                    <Grid item>
                      <Typography variant="body2" fontWeight="bold">
                        {symbolMarketData?.volume || 'N/A'}
                      </Typography>
                      <Typography variant="caption">Volume</Typography>
                    </Grid>
                    <Grid item>
                      <Typography variant="body2" fontWeight="bold">
                        {symbolMarketData?.bid_price || 'N/A'}
                      </Typography>
                      <Typography variant="caption">Last Price</Typography>
                    </Grid>
                    <Grid item>
                      <Typography variant="body2" fontWeight="bold">
                        {symbolMarketData?.change_percent || 'N/A'}
                      </Typography>
                      <Typography variant="caption">%Change</Typography>
                    </Grid>
                  </Grid>

                  <Divider />

                  <Grid container justifyContent="space-between" alignItems="center" mt={1}>
                    <Grid item>
                      <Box display="flex" alignItems="center" gap={0.5}>
                      {wsConnected && (
                          <AccessTimeIcon 
                            sx={{ 
                              fontSize: 14,
                              color: 'default.main'
                            }} 
                          />
                        )}
                        <Typography variant="caption">
                          {wsConnected ? "Realtime" : "N/A"}
                        </Typography>
                        
                      </Box>
                    </Grid>
                    <Grid item>
                      <Typography variant="caption" textAlign="right">
                        Exchange: {values.symbol ? values.symbol.exchange : 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Options-specific fields */}
              {tradeType === 'options' && (
                <Card variant="outlined" sx={{ mt: 2, mb: 2 }}>
                  <CardContent sx={{ p: '12px!important' }}>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      Option Contract Selection
                      {isLoadingOptionsChain && (
                        <CircularProgress size={16} sx={{ ml: 1 }} />
                      )}
                    </Typography>
                    
                    {!values.symbol && (
                      <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic', color: 'text.secondary' }}>
                        Select an equity above to view available options
                      </Typography>
                    )}
                    
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <MuiTextField
                          select
                          label="Strike Price"
                          value={selectedStrike || ''}
                          onChange={(e) => setSelectedStrike(e.target.value)}
                          size="small"
                          fullWidth
                          disabled={isLoadingOptionsChain || !optionsChainData || !values.symbol}
                        >
                          {optionsChainData?.strikes?.[selectedExpiry]?.map((strike) => (
                            <MenuItem key={strike} value={strike}>
                              ${strike}
                            </MenuItem>
                          )) || []}
                        </MuiTextField>
                      </Grid>
                      
                      <Grid item xs={4}>
                        <ToggleButtonGroup
                          value={selectedRight}
                          exclusive
                          onChange={(event, newRight) => {
                            if (newRight !== null) {
                              setSelectedRight(newRight);
                            }
                          }}
                          size="small"
                          fullWidth
                          disabled={isLoadingOptionsChain || !optionsChainData || !values.symbol}
                        >
                          <ToggleButton value="C" sx={{ flexGrow: 1 }}>
                            Call
                          </ToggleButton>
                          <ToggleButton value="P" sx={{ flexGrow: 1 }}>
                            Put
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Grid>
                      
                      <Grid item xs={4}>
                        <MuiTextField
                          select
                          label="Expiry Date"
                          value={selectedExpiry || ''}
                          onChange={(e) => setSelectedExpiry(e.target.value)}
                          size="small"
                          fullWidth
                          disabled={isLoadingOptionsChain || !optionsChainData || !values.symbol}
                        >
                          {optionsChainData?.expirations?.map((expiry) => (
                            <MenuItem key={expiry} value={expiry}>
                              {formatExpirationDate(expiry)}
                            </MenuItem>
                          )) || []}
                        </MuiTextField>
                      </Grid>
                    </Grid>
                    
                    {selectedStrike && selectedExpiry && selectedRight && values.symbol && (
                      <Box sx={{ mt: 2, p: 1, backgroundColor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="body2" textAlign="center">
                          Selected: {values.symbol.name} {formatExpirationDate(selectedExpiry)} ${selectedStrike} {selectedRight === 'C' ? 'Call' : 'Put'}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Action (Buy/Sell) Toggle */}
              <ToggleButtonGroup
                value={values.side}
                exclusive
                onChange={(event, newSide) => {
                  if (newSide !== null && !isModifyingOrder) {
                    setFieldValue('side', newSide);
                  }
                }}
                fullWidth
                disabled={isModifyingOrder}
              >
                <ToggleButton value="BUY" sx={{ flexGrow: 1, color: values.side === 'buy' ? 'green' : 'inherit' }}>
                  BUY
                </ToggleButton>
                <ToggleButton value="SELL" sx={{ flexGrow: 1, color: values.side === 'sell' ? 'red' : 'inherit' }}>
                  SELL
                </ToggleButton>
              </ToggleButtonGroup>

              {/* Order Form Fields */}
              <Grid container spacing={2} mt={2}>
                <Grid item xs={3}>
                  <Field
                    component={TextField}
                    select
                    name="order_type"
                    label="Order Type"
                    size="small"
                    fullWidth
                    disabled={isModifyingOrder}
                  >
                    <MenuItem value="MKT">Market</MenuItem>
                    <MenuItem value="LMT">Limit</MenuItem>
                    <MenuItem value="STP">Stop</MenuItem>
                  </Field>
                </Grid>

                <Grid item xs={3}>
                  <Field
                    component={TextField}
                    name="quantity"
                    id="quantity"
                    label="Quantity"
                    type="number"
                    fullWidth
                    size="small"
                    inputProps={{ min: 1, step: 1 }}
                  />
                </Grid>

                <Grid item xs={3}>
                  <Field
                    component={TextField}
                    name="price"
                    label="Price"
                    type="number"
                    fullWidth
                    size="small"
                    inputProps={{ min: 0.01, step: 0.01 }}
                    disabled={values.order_type === 'MKT'}
                  />
                </Grid>

                <Grid item xs={3}>
                  <Field
                    component={TextField}
                    name="tif"
                    label="TIF"
                    select
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="day">Day</MenuItem>
                    <MenuItem value="gtc">GTC</MenuItem>
                  </Field>
                </Grid>
              </Grid>

              {/* Order Summary Text */}
              <Box mt={2}>
                <Card variant="outlined">
                  <Typography variant="body1" textAlign="center">
                    {tradeType === 'options' && selectedStrike && selectedExpiry && selectedRight ? (
                      `Order = ${values.side?.toUpperCase()} ${values.quantity || 'N/A'} ${values.symbol?.name || 'N/A'} ${formatExpirationDate(selectedExpiry)} ${selectedRight === 'C' ? 'C' : 'P'} ${selectedStrike} @ ${values.price || 'N/A'} ${values.order_type} ${values.tif}`
                    ) : (
                      `Order = ${values.side?.toUpperCase()} ${values.quantity || 'N/A'} ${values.symbol?.label || 'N/A'} @ ${values.price || 'N/A'} ${values.order_type} ${values.tif}`
                    )}
                  </Typography>
                </Card>
              </Box>

              {/* Buttons */}
              <Grid container spacing={2} mt={2}>
                <Grid item xs={6}>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    onClick={() => setAdvancedVisible(!isAdvancedVisible)}
                    disabled={isModifyingOrder}
                  >
                    Advanced
                  </Button>
                </Grid>

                <Grid item xs={6}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    sx={{
                      backgroundColor: values.side === 'BUY' ? 'green' : 'red',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: values.side === 'BUY' ? '#005500' : '#880000',
                      },
                    }}
                    disabled={!values.symbol || isSubmitting || ordersLoading}
                  >
                    {isSubmitting ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      getButtonText(values.side)
                    )}
                  </Button>
                </Grid>
              </Grid>

              {/* Advanced Section */}
              {isAdvancedVisible && (
                <Box mt={2}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <FormControlLabel
                          control={
                            <Field
                              as={Checkbox}
                              name="isStopLossEnabled"
                              checked={values.isStopLossEnabled}
                              onChange={(event) => setFieldValue('isStopLossEnabled', event.target.checked)}
                              disabled={isModifyingOrder}
                            />
                          }
                          label="Stop Loss"
                        />
                        <Field
                          component={TextField}
                          name="stop_loss"
                          label="Stop Loss"
                          type="number"
                          fullWidth
                          size="small"
                          inputProps={{ step: 0.01 }}
                          disabled={!values.isStopLossEnabled || isModifyingOrder}
                        />
                      </Grid>

                      <Grid item xs={6}>
                        <FormControlLabel
                          control={
                            <Field
                              as={Checkbox}
                              name="isProfitTargetEnabled"
                              checked={values.isProfitTargetEnabled}
                              onChange={(event) => setFieldValue('isProfitTargetEnabled', event.target.checked)}
                              disabled={isModifyingOrder}
                            />
                          }
                          label="Profit Target"
                        />
                        <Field
                          component={TextField}
                          name="profit_target"
                          label="Profit Target"
                          type="number"
                          fullWidth
                          size="small"
                          inputProps={{ step: 0.01 }}
                          disabled={!values.isProfitTargetEnabled || isModifyingOrder}
                        />
                      </Grid>
                    </Grid>

                    <Box mt={2}>
                      <Field
                        component={TextField}
                        name="order_comment"
                        label="Order Comment"
                        multiline
                        fullWidth
                        size="small"
                        disabled={isModifyingOrder}
                      />
                    </Box>
                  </Card>
                </Box>
              )}
            </Box>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default TradePanel;